import { TFunction } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';

import { GridValueFormatter } from '@mui/x-data-grid-pro';

import { Country, Product, ShipmentExactResponseProduct } from '@libraries/api/gen-api';
import {
  CountryResponseServiceAdmin,
  NetsuiteOrderType,
  Organization,
  OrganizationProductInventory,
  Product as AdminProduct,
  PurchaseOrderCreateRequest,
  RefundReasonResponse,
} from '@libraries/api/gen-api-admin';
import { GrantStatusEnum } from '@libraries/api/gen-api-hidden';

import {
  AdminOrganizationMap,
  AllInventoryMap,
  AllRoles,
  AllShipmentStatuses,
  EditorEvents,
  ParserResult,
  type ReadableFileSize,
  RefundReasonSelection,
  RequestState,
  SearchParams,
} from '../types/types';

export const isProduction = window.fromServer?.env?.SENTRY_STAGE === 'prod'; // Not for testing environments

export const isProd = process.env.NODE_ENV === 'production';
export const isServiceConsole: boolean = window.fromServer?.env?.IS_SERVICE_CONSOLE;
export const isTestEnv = window.fromServer?.env?.IS_TEST_ENV;
export const isDev = !isProd;
export const inHouse = 'In House';
export const ADD_RULE = 'add-rule';
export const EDIT_RULE = 'edit-rule';
export const YUBICO_CHANNEL_PARTNER = 1;
export const REFUND_TYPE_PRODUCT = 'product';
export const REFUND_TYPE_SHIPPING = 'shipping';
export const MAX_SHIPPING_KEYS = 500;
export const MODE_ADD: Mode = 'add';
export const MODE_EDIT: Mode = 'edit';
export const CONSOLE_REFERRER = 'console';
export const SERVICE_CONSOLE_REFERRER = 'service_console';
export const NUMBER_LIMIT = 2147483647;
export const PRIORITY_LIMIT = 99999;
export const FPR_LABEL = 'Auto FIDO Pre-reg';
export const NO_VALUE_PLACEHOLDER = '--';
export const ZENDESK_TICKET_TYPE = 'zendesk';

export const SSODocLink = () => window.fromServer.env.SAML_SSO_LINK;

export const ShipmentStatus = {
  ShipmentStateIncomplete: 1,
  ShipmentStateDraft: 2,
  ShipmentStateAwaitingValidation: 3,
  ShipmentStateProcessingAddress: 4,
  ShipmentStateAddressValid: 5,
  ShipmentStateAddressInvalid: 6,
  ShipmentStateAddressFail: 7,
  ShipmentStateError: 8,
  ShipmentStateDPLMatch: 9,
  ShipmentStateAwaitingEstimate: 10,
  ShipmentStateAwaitingEstimateApproval: 11,
  ShipmentStateRequested: 12,
  ShipmentStateAwaitingDPLVerification: 13,
  ShipmentStateUnableToFormatAddress: 14,
};

export const CanEditShipment = (state: number) =>
  [
    ShipmentStatus.ShipmentStateIncomplete,
    ShipmentStatus.ShipmentStateDraft,
    ShipmentStatus.ShipmentStateAwaitingValidation,
    ShipmentStatus.ShipmentStateProcessingAddress,
    ShipmentStatus.ShipmentStateAddressValid,
    ShipmentStatus.ShipmentStateAddressInvalid,
    ShipmentStatus.ShipmentStateAddressFail,
    ShipmentStatus.ShipmentStateUnableToFormatAddress,
    ShipmentStatus.ShipmentStateError,
    ShipmentStatus.ShipmentStateAwaitingEstimate,
    ShipmentStatus.ShipmentStateAwaitingEstimateApproval,
    ShipmentStatus.ShipmentStateAwaitingDPLVerification,
  ].includes(state);

export const STATES_THAT_DEDUCT_ALLOCATION = [
  AllShipmentStatuses.ShipmentStateAwaitingValidation,
  AllShipmentStatuses.ShipmentStateProcessingAddress,
  AllShipmentStatuses.ShipmentStateAddressValid,
  AllShipmentStatuses.ShipmentStateDPLMatch,
  AllShipmentStatuses.ShipmentStateAwaitingDPLVerification,
  AllShipmentStatuses.ShipmentStateProcessingShipment,
  AllShipmentStatuses.ShipmentStateFulfillmentReady,
  AllShipmentStatuses.ShipmentStateProcessingFulfillment,
  AllShipmentStatuses.ShipmentStateShipped,
  AllShipmentStatuses.ShipmentStateDelivered,
  AllShipmentStatuses.ShipmentStateLost,
  AllShipmentStatuses.ShipmentStateDeliveryException,
  AllShipmentStatuses.ShipmentStateReturnedToSender,
  AllShipmentStatuses.ShipmentStateManualFulfillment,
  AllShipmentStatuses.ShipmentStateQueuedForFulfillment,
] as const;

export enum AuthenticationMethod {
  DEMO = 'yed_demo',
  WEBAUTHN = 'yed_credentials',
  MACHINETOKEN = 'machine_token',
  SSO = 'organization_sso',
  IAP = 'gcp_iap',
  PASSWORDLESS = 'passkey',
}

export enum SHIPPING_TYPE {
  AUTO = 'automated',
  MANUAL = 'manual',
  FPR = 'fido2preregistered',
}

export enum ERROR_TYPE {
  BADREQUEST = 'bad_request',
  UNAUTHORIZED = 'unauthorized',
  NOTFOUND = 'not_found',
}
export const DPL_OVERRIDE = 'dpl-override';
export const ADDRESS_OVERRIDE = 'address-override';
export const STATE_OVERRIDE = 'state-override';

export const isDPL = (state: number) =>
  [AllShipmentStatuses.ShipmentStateDPLMatch, AllShipmentStatuses.ShipmentStateDPLConfirmed].includes(state);

export const HasShipmentError = (state: number) =>
  [
    AllShipmentStatuses.ShipmentStateIncomplete,
    AllShipmentStatuses.ShipmentStateDraft,
    AllShipmentStatuses.ShipmentStateAddressFail,
    AllShipmentStatuses.ShipmentStateAddressInvalid,
    AllShipmentStatuses.ShipmentStateUnableToFormatAddress,
    AllShipmentStatuses.ShipmentStateError,
    AllShipmentStatuses.ShipmentStateDPLMatch,
    AllShipmentStatuses.ShipmentStatePanic,
    AllShipmentStatuses.ShipmentStateLost,
    AllShipmentStatuses.ShipmentStateDeliveryException,
    AllShipmentStatuses.ShipmentStateDPLConfirmed,
    AllShipmentStatuses.ShipmentStateReturnedToSender,
  ].includes(state);
export const isManualFulfillment = (state: number) => state === 2000;
// scp = user.token.scp
export const CanEditPurchaseOrder = (scp: string) => scp.startsWith('service-');

export const centToDollar = (cents: number): string => {
  if (cents === undefined) {
    return '$0';
  }
  const dollars = cents === 0 ? 0 : cents / 100;
  try {
    return Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(dollars);
  } catch {
    return `$${dollars}`;
  }
};

export const getRoleName = (roleSlug: string): string => {
  const map: Record<string, string> = {
    'org-owner': 'YubiEnterprise Console Owner',
    'org-shipping-admin': 'YubiEnterprise Console Admin',
    'org-auditor': 'YubiEnterprise Console Auditor',
    'service-owner': 'Service Owner',
    'service-admin': 'Service Admin',
    'service-auditor': 'Service Auditor',
    'role-none': 'None',
    'org-distributor': 'Distributor',
    'org-reseller': 'Reseller',
  };

  if (map[roleSlug]) {
    return map[roleSlug];
  }
  throw new Error('Role does not have a name');
};

export const getRoleNames = (roles: string[]) => roles?.map((r) => getRoleName(r)).join(', ');

export const getRoleLabel = (roleSlug: AllRoles): string => {
  const map: Record<AllRoles, string> = {
    'org-owner': 'Console Owner',
    'org-shipping-admin': 'Console Admin',
    'org-auditor': 'Console Auditor',
    'service-owner': 'Service Owner',
    'service-admin': 'Service Admin',
    'service-auditor': 'Service Auditor',
    'org-distributor': 'Distributor',
    'org-reseller': 'Reseller',
  };
  if (isEmpty(roleSlug)) {
    return '';
  }
  if (map[roleSlug]) {
    return map[roleSlug];
  }
  throw new Error('Role does not have a label');
};

// TODO: Temporary holds the values, switch this out with an API later
export const getDeliveryType = (id: number) => {
  switch (id) {
    case 1:
      return 'Normal';
    case 2:
      return 'Expedited';
    default:
      return 'Unknown';
  }
};

/*
  1: 'YubiKey Shipment',
  2: 'YubiKey Tier SKU Shipment',
  3: 'Subscription - Initial Shipment',
  4: 'Subscription - Buffer Stock Shipment',
  5: 'Subscription - Replacement YubiKey Shipment',
*/

export const InventoryTypeEnum = {
  1: 'Physical products',
  2: 'YubiKey Tier SKU Shipment',
  3: 'Subscription - Initial Shipment',
  4: 'Subscription - Buffer Stock Shipment',
  5: 'Subscription - Replacement YubiKey Shipment',
};

export function getTypesThatSupportDelete() {
  return [SHIPPING_TYPE.AUTO, SHIPPING_TYPE.FPR];
}

export function getStatesThatSupportDelete() {
  return [
    ShipmentStatus.ShipmentStateIncomplete,
    ShipmentStatus.ShipmentStateDraft,
    ShipmentStatus.ShipmentStateAwaitingValidation,
    ShipmentStatus.ShipmentStateProcessingAddress,
    ShipmentStatus.ShipmentStateAddressValid,
    ShipmentStatus.ShipmentStateAddressInvalid,
    ShipmentStatus.ShipmentStateAddressFail,
    ShipmentStatus.ShipmentStateUnableToFormatAddress,
    ShipmentStatus.ShipmentStateError,
    ShipmentStatus.ShipmentStateAwaitingEstimate,
    ShipmentStatus.ShipmentStateAwaitingEstimateApproval,
    ShipmentStatus.ShipmentStateAwaitingDPLVerification,
  ];
}

export function getStatesThatSupportAddressOverride() {
  return [AllShipmentStatuses.ShipmentStateAddressInvalid, AllShipmentStatuses.ShipmentStateAddressFail];
}

export function getTypesThatSupportAddressOverride() {
  return [SHIPPING_TYPE.AUTO, SHIPPING_TYPE.FPR];
}

export function getShipmentStateMessages(): Array<[number, string]> {
  return [
    [AllShipmentStatuses.ShipmentStateAddressValid, 'Accepted for Fulfillment'],
    [AllShipmentStatuses.ShipmentStateAddressFail, 'Address Validation Failed'],
    [AllShipmentStatuses.ShipmentStateAwaitingValidation, 'Awaiting Validation'],
    [AllShipmentStatuses.ShipmentStateAwaitingDPLVerification, 'Awaiting DPL Verification'],
    [AllShipmentStatuses.ShipmentStateUnableToFormatAddress, 'Address Not Accepted by Carrier'],
    [AllShipmentStatuses.ShipmentStateCancelled, 'Cancelled'],
    [AllShipmentStatuses.ShipmentStateDPLConfirmed, 'DPL Confirmed'],
    [AllShipmentStatuses.ShipmentStateDelivered, 'Delivered'],
    [AllShipmentStatuses.ShipmentStateDeliveryException, 'Delivery Exception'],
    [AllShipmentStatuses.ShipmentStateDPLMatch, 'Error: DPL Match'],
    [AllShipmentStatuses.ShipmentStateError, 'Error: Processing Error, contact Support'],
    [AllShipmentStatuses.ShipmentStatePanic, 'Error: Shipping error, contact Support'],
    [AllShipmentStatuses.ShipmentStateAddressInvalid, 'Incomplete'],
    [AllShipmentStatuses.ShipmentStateIncomplete, 'Incomplete Shipping Request'],
    [AllShipmentStatuses.ShipmentStateManualFulfillment, 'Manual Processing'],
    [AllShipmentStatuses.ShipmentStateQueuedForFulfillment, 'Processing: Queued for Fulfillment'],
    [AllShipmentStatuses.ShipmentStateFulfillmentReady, 'Processing: Ready for Fulfillment'],
    [AllShipmentStatuses.ShipmentStateProcessingFulfillment, 'Processing: Sent for Fulfillment'],
    [AllShipmentStatuses.ShipmentStateRequested, 'Requested'],
    [AllShipmentStatuses.ShipmentStateReturnedToSender, 'Returned to Sender'],
    [AllShipmentStatuses.ShipmentStateLost, 'Shipment Lost/Missing'],
    [AllShipmentStatuses.ShipmentStateShipped, 'Shipped: In transit'],
  ];
}

interface ProductTierEnumProps {
  [id: number]: string;
}

export const ProductTierEnum: ProductTierEnumProps = {
  1: 'Base Tier',
  2: 'Advanced Tier',
  3: 'Premium Tier',
  4: 'FIPS Tier',
  5: '2.0 Base Tier',
  6: '2.0 Base/Bio Tier',
  7: '2.0 Advanced Tier',
  8: '2.0 Advanced/Bio Tier',
  9: '2.0 Compliance Tier',
};

export const AllProductTierEnum: ProductTierEnumProps = {
  0: 'No Tier',
  ...ProductTierEnum,
};

export const customProductRedeemTierOptions = [
  { id: '1', value: '1', text: ProductTierEnum[1] },
  { id: '2', value: '2', text: ProductTierEnum[2] },
  { id: '3', value: '3', text: ProductTierEnum[3] },
  { id: '4', value: '4', text: ProductTierEnum[4] },
  { id: '5', value: '5', text: ProductTierEnum[5] },
  { id: '6', value: '6', text: ProductTierEnum[6] },
  { id: '7', value: '7', text: ProductTierEnum[7] },
  { id: '8', value: '8', text: ProductTierEnum[8] },
  { id: '9', value: '9', text: ProductTierEnum[9] },
];
export const ProductTierCount = Object.keys(ProductTierEnum).length;

interface SubscriptionTypeEnumProps {
  [id: number]: string;
}

export const SubscriptionTypeEnum: SubscriptionTypeEnumProps = {
  0: 'N/A (no subscription)',
  1: 'Primary subscription',
  2: 'Backup subscription',
  3: 'Primary Standard subscription 2.0',
  4: 'Backup Standard subscription 2.0',
  5: 'Primary Plus subscription 2.0',
  6: 'Backup Plus subscription 2.0',
};

// Concatenates `additional` to `url` ensuring there's only one slash between them
export function composeURL(url: string, additional: string): string {
  return `${url}/${additional}`.replace(/\/+/g, '/');
}

/*
  Returns the sort order based on expensive to least expensive
  FIPS tier ... base tier
  Subscription, non-subscription, Physical products
*/

export const ProductImages = [
  // Standard YubiKeys
  { image_code: 'yk5nfc_img', image_name: 'YubiKey 5 NFC' },
  { image_code: 'yk5nano_img', image_name: 'YubiKey 5 Nano' },
  { image_code: 'yknanofips_img', image_name: 'YubiKey Nano FIPS' },
  { image_code: 'yk5c_img', image_name: 'YubiKey 5C' },
  { image_code: 'yk5cnano_img', image_name: 'YubiKey 5C Nano' },
  { image_code: 'ykcnanofips_img', image_name: 'YubiKey C Nano FIPS' },
  { image_code: 'sky2_img', image_name: 'Security Key' },
  { image_code: 'sky3_img', image_name: 'Security Key NFC' },
  { image_code: 'sky3c_img', image_name: 'Security Key C NFC' },
  { image_code: 'yk5ci_img', image_name: 'YubiKey 5Ci' },
  { image_code: 'ykfips_img', image_name: 'YubiKey FIPS' },
  { image_code: 'ykcfips_img', image_name: 'YubiKey C FIPS' },
  { image_code: 'yk5cnfc_img', image_name: 'YubiKey 5C NFC' },
  { image_code: 'ykbioa_img', image_name: 'YubiKey BIO' },
  { image_code: 'ykbioc_img', image_name: 'YubiKey BIO C' },
  // Lanyards & Keyport Accessories
  { image_code: 'yklanyard_img', image_name: 'YubiKey Lanyard' },
  { image_code: 'keyport_lanyard_img', image_name: 'Yubico x Keyport ParaPull Lanyard' },
  { image_code: 'keyport_pivot_img', image_name: 'Yubico x Keyport Pivot 2.0 key organizer' },
  // YubiStyle Covers - A/C NFC
  {
    image_code: 'yubistyle_yk5_aloha_lavender_img',
    image_name: 'YubiStyle Cover - A/C NFC - Aloha Lavender',
  },
  { image_code: 'yubistyle_yk5_blue_img', image_name: 'YubiStyle Cover - A/C NFC - Blue' },
  { image_code: 'yubistyle_yk5_cats_img', image_name: 'YubiStyle Cover - A/C NFC - YubiCats' },
  { image_code: 'yubistyle_yk5_cyber_sunrise_img', image_name: 'YubiStyle Cover - A/C NFC - Cyber Sunrise' },
  { image_code: 'yubistyle_yk5_diamond_prism_img', image_name: 'YubiStyle Cover - A/C NFC - Diamond Prism' },
  { image_code: 'yubistyle_yk5_dogs_img', image_name: 'YubiStyle Cover - A/C NFC - YubiDogs' },
  { image_code: 'yubistyle_yk5_geode_blue_img', image_name: 'YubiStyle Cover - A/C NFC - Geode Blue' },
  { image_code: 'yubistyle_yk5_green_img', image_name: 'YubiStyle Cover - A/C NFC - Green' },
  {
    image_code: 'yubistyle_yk5_infinite_green_img',
    image_name: 'YubiStyle Cover - A/C NFC - Infinite Green',
  },
  { image_code: 'yubistyle_yk5_polka_blue_img', image_name: 'YubiStyle Cover - A/C NFC - Polka Blue' },
  { image_code: 'yubistyle_yk5_polka_green_img', image_name: 'YubiStyle Cover - A/C NFC - Polka Green' },
  { image_code: 'yubistyle_yk5_polka_red_img', image_name: 'YubiStyle Cover - A/C NFC - Polka Red' },
  { image_code: 'yubistyle_yk5_purple_img', image_name: 'YubiStyle Cover - A/C NFC - Purple' },
  { image_code: 'yubistyle_yk5_rainbow_img', image_name: 'YubiStyle Cover - A/C NFC - Double Rainbow' },
  { image_code: 'yubistyle_yk5_red_img', image_name: 'YubiStyle Cover - A/C NFC - Red' },
  { image_code: 'yubistyle_yk5_tie_dye_img', image_name: 'YubiStyle Cover - A/C NFC - Tie Dye' },
  { image_code: 'yubistyle_yk5_urban_camo_img', image_name: 'YubiStyle Cover - A/C NFC - Urban Camo' },
  // YubiStyle Covers - C
  { image_code: 'yubistyle_yk5c_blue_img', image_name: 'YubiStyle Cover - C - Blue' },
  { image_code: 'yubistyle_yk5c_diamond_prism_img', image_name: 'YubiStyle Cover - C - Diamond Prism' },
  { image_code: 'yubistyle_yk5c_geode_blue_img', image_name: 'YubiStyle Cover - C - Geode Blue' },
  { image_code: 'yubistyle_yk5c_green_img', image_name: 'YubiStyle Cover - C - Green' },
  { image_code: 'yubistyle_yk5c_polka_blue_img', image_name: 'YubiStyle Cover - C - Polka Blue' },
  { image_code: 'yubistyle_yk5c_polka_green_img', image_name: 'YubiStyle Cover - C - Polka Green' },
  { image_code: 'yubistyle_yk5c_polka_red_img', image_name: 'YubiStyle Cover - C - Polka Red' },
  { image_code: 'yubistyle_yk5c_purple_img', image_name: 'YubiStyle Cover - C - Purple' },
  { image_code: 'yubistyle_yk5c_rainbow_img', image_name: 'YubiStyle Cover - C - Double Rainbow' },
  { image_code: 'yubistyle_yk5c_red_img', image_name: 'YubiStyle Cover - C - Red' },
  { image_code: 'yubistyle_yk5c_urban_camo_img', image_name: 'YubiStyle Cover - C - Urban Camo' },
  // YubiStyle Covers - Ci
  { image_code: 'yubistyle_yk5ci_blue_img', image_name: 'YubiStyle Cover - Ci - Blue' },
  { image_code: 'yubistyle_yk5ci_green_img', image_name: 'YubiStyle Cover - Ci - Green' },
  { image_code: 'yubistyle_yk5ci_polka_blue_img', image_name: 'YubiStyle Cover - Ci - Polka Blue' },
  { image_code: 'yubistyle_yk5ci_polka_green_img', image_name: 'YubiStyle Cover - Ci - Polka Green' },
  { image_code: 'yubistyle_yk5ci_polka_red_img', image_name: 'YubiStyle Cover - Ci - Polka Red' },
  { image_code: 'yubistyle_yk5ci_purple_img', image_name: 'YubiStyle Cover - Ci - Purple' },
  { image_code: 'yubistyle_yk5ci_rainbow_img', image_name: 'YubiStyle Cover - Ci - Double Rainbow' },
];

export const VirtualProductImages = [
  {
    image_code: 'atiersub_img',
    image_name: 'Advanced tier subscription',
  },
  {
    image_code: 'btiersub_img',
    image_name: 'Basic tier subscription',
  },
  {
    image_code: 'ftiersub_img',
    image_name: 'FIPS tier subscription',
  },
  {
    image_code: 'ftierbiosub_img',
    image_name: 'FIPS tier subscription (Bio)',
  },
  {
    image_code: 'ptiersub_img',
    image_name: 'Premium tier subscription',
  },
  {
    image_code: 'ptierbiosub_img',
    image_name: 'Premium tier subscription (Bio)',
  },
  {
    image_code: '2basesub_img',
    image_name: 'Base 2.0 subscription',
  },
  {
    image_code: '2basebiosub_img',
    image_name: 'Base/Bio subscription',
  },
  {
    image_code: '2advsub_img',
    image_name: 'Advanced 2.0 subscription',
  },
  {
    image_code: '2advbiosub_img',
    image_name: 'Advanced/Bio subscription',
  },
  {
    image_code: '2compsub_img',
    image_name: 'Compliance subscription',
  },
];

const defaultAddressValidationCountryList = {
  US: 'United States',
  SE: 'Sweden',
  AT: 'Austria',
  AU: 'Australia',
  BE: 'Belgium',
  BG: 'Bulgaria',
  CA: 'Canada',
  CH: 'Switzerland',
  CL: 'Chile',
  CY: 'Cyprus',
  CZ: 'Czechia',
  DE: 'Germany',
  DK: 'Denmark',
  EE: 'Estonia',
  ES: 'Spain',
  FI: 'Finland',
  FR: 'France',
  GB: 'United Kingdom',
  GR: 'Greece',
  HK: 'Hong Kong',
  HR: 'Croatia',
  HU: 'Hungary',
  ID: 'Indonesia',
  IE: 'Ireland',
  IL: 'Israel',
  IS: 'Iceland',
  IT: 'Italy',
  JP: 'Japan',
  LI: 'Liechtenstein',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  LV: 'Latvia',
  MT: 'Malta',
  MX: 'Mexico',
  NL: 'Netherlands',
  NO: 'Norway',
  NZ: 'New Zealand',
  PH: 'Philippines',
  PL: 'Poland',
  PT: 'Portugal',
  RO: 'Romania',
  SA: 'Saudi Arabia',
  SG: 'Singapore',
  SK: 'Slovakia',
  SI: 'Slovenia',
  TH: 'Thailand',
  TW: 'Taiwan (Province of China)',
  AE: 'United Arab Emirates',
};

// certain countries have default address validation and certain countries prefer maxLength
// This function returns default list
// TODO: API improvement to return is_default, is_manual
export const isDefaultAddressValidation = (code: string) =>
  code?.toUpperCase() in defaultAddressValidationCountryList;

// Default field constraints applied to most countries
export const defaultAddressFormLengths = {
  lenStreetLine: 60,
  lenCity: 60,
  lenRegion: 50,
  lenFirstname: 15,
  lenLastname: 20,
  lenRecipient: 20,
  lenEmail: 80,
  lenTelephone: 40,
  lenPostalCode: 50,
  lenComment: 300,
};

// Relaxed field constraints
export const relaxedAddressFormLengths = {
  lenStreetLine: 255,
  lenCity: 255,
  lenRegion: 255,
  lenFirstname: 255,
  lenLastname: 255,
  lenRecipient: 255,
  lenEmail: 255,
  lenTelephone: 255,
  lenPostalCode: 255,
  lenComment: 300,
};

export const commentLength = 300;
export const emailLength = 255;

export const isStandardProduct = (product: {
  is_virtual_product?: boolean;
  is_subscription_product?: boolean;
}) => !product?.is_virtual_product && !product?.is_subscription_product;

export const findProductById = (products: Product[] | AdminProduct[], productID: number) =>
  (products as (AdminProduct | Product)[])?.find((p: AdminProduct | Product) => p?.product_id === productID);

export const isStandard = (products: Product[] | AdminProduct[], productID: number) => {
  const product = findProductById(products, productID);
  return product ? isStandardProduct(product) : false;
};

/**
 * Returns the OrganizationProductInventory Array
 * @function - To filter inventory based on products available
 * @param {OrganizationProductInventory[]} inventrory - inventory of the organization
 * @param {Product[] | AdminProduct[]} products - Products available to organization
 * @returns {OrganizationProductInventory[]} - Returns list of organizationProductArray
 */

export function filterInventoryByAvailableProducts(
  inventory: OrganizationProductInventory[],
  products: Product[] | AdminProduct[]
): OrganizationProductInventory[] {
  return inventory.filter((inv: OrganizationProductInventory) => {
    const product = findProductById(products, inv?.product_id);
    return product !== undefined;
  });
}

/**
 * Returns the Array of ShipmentExactResponseProduct filtered by products available
 * @function - To filter shipments based on inventory available
 * @param {Array<ShipmentExactResponseProduct>} shipmentItems - Array of items in shipment
 * @param {OrganizationProductInventory[]} inventory - Inventory available to organization
 * @returns {Array<ShipmentExactResponseProduct>} - Filtered ShipmentExactResponseProduct
 * by available products
 */

export function filterShipmentsByInventory(
  shipmentItems: Array<ShipmentExactResponseProduct>,
  inventory: OrganizationProductInventory[]
): Array<ShipmentExactResponseProduct> {
  return shipmentItems.filter((ship: ShipmentExactResponseProduct) =>
    inventory.find(
      (inv: OrganizationProductInventory) =>
        inv?.product_id === ship?.inventory_product_id && inv?.product_mapping?.includes(ship?.product_id)
    )
  );
}

/**
 *
 * @param upperBound non-inclusive upper bound
 * @returns a random number between 0 and the upperBound
 */
export const getRandomInt = (upperBound: number) => Math.floor(Math.random() * upperBound);

/**
 *
 * @param value the string value to parse
 * @param def the default value to return in the case that the parse value is invalid.
 *  Defaults to 0.
 * @returns The absolute value of the parsed integer or the default value in the case
 * that the parsed value is NaN or invalid
 */
export const parseAbsIntOrDefault = (value: string, def = 0) => {
  const parsed = parseInt(value, 10);
  return Number.isInteger(parsed) ? Math.abs(parsed) : def;
};

export const ManualShipmentTransitions = new Map([
  [
    AllShipmentStatuses.ShipmentStateRequested,
    [
      AllShipmentStatuses.ShipmentStateShipped,
      AllShipmentStatuses.ShipmentStateDelivered,
      AllShipmentStatuses.ShipmentStateCancelled,
    ],
  ],
  [
    AllShipmentStatuses.ShipmentStateShipped,
    [
      AllShipmentStatuses.ShipmentStateShipped,
      AllShipmentStatuses.ShipmentStateDelivered,
      AllShipmentStatuses.ShipmentStateCancelled,
      AllShipmentStatuses.ShipmentStateDeliveryException,
      AllShipmentStatuses.ShipmentStateLost,
      AllShipmentStatuses.ShipmentStateReturnedToSender,
    ],
  ],
  [
    AllShipmentStatuses.ShipmentStateDelivered,
    [
      AllShipmentStatuses.ShipmentStateDelivered,
      AllShipmentStatuses.ShipmentStateDeliveryException,
      AllShipmentStatuses.ShipmentStateLost,
      AllShipmentStatuses.ShipmentStateReturnedToSender,
      AllShipmentStatuses.ShipmentStateShipped,
    ],
  ],
  [
    AllShipmentStatuses.ShipmentStateLost,
    [
      AllShipmentStatuses.ShipmentStateLost,
      AllShipmentStatuses.ShipmentStateDelivered,
      AllShipmentStatuses.ShipmentStateDeliveryException,
      AllShipmentStatuses.ShipmentStateReturnedToSender,
    ],
  ],
  [
    AllShipmentStatuses.ShipmentStateReturnedToSender,
    [
      AllShipmentStatuses.ShipmentStateReturnedToSender,
      AllShipmentStatuses.ShipmentStateDelivered,
      AllShipmentStatuses.ShipmentStateDeliveryException,
      AllShipmentStatuses.ShipmentStateLost,
    ],
  ],
  [
    AllShipmentStatuses.ShipmentStateDeliveryException,
    [
      AllShipmentStatuses.ShipmentStateDeliveryException,
      AllShipmentStatuses.ShipmentStateDelivered,
      AllShipmentStatuses.ShipmentStateLost,
      AllShipmentStatuses.ShipmentStateReturnedToSender,
      AllShipmentStatuses.ShipmentStateShipped,
    ],
  ],
  [AllShipmentStatuses.ShipmentStateCancelled, []],
]);

export const ShipmentAllowedTransitions = new Map([
  [
    AllShipmentStatuses.ShipmentStateShipped,
    [
      AllShipmentStatuses.ShipmentStateDelivered,
      AllShipmentStatuses.ShipmentStateLost,
      AllShipmentStatuses.ShipmentStateDeliveryException,
      AllShipmentStatuses.ShipmentStateReturnedToSender,
      AllShipmentStatuses.ShipmentStateCancelled,
    ],
  ],
  [
    AllShipmentStatuses.ShipmentStateDelivered,
    [
      AllShipmentStatuses.ShipmentStateLost,
      AllShipmentStatuses.ShipmentStateDeliveryException,
      AllShipmentStatuses.ShipmentStateReturnedToSender,
      AllShipmentStatuses.ShipmentStateShipped,
    ],
  ],
  [
    AllShipmentStatuses.ShipmentStateLost,
    [
      AllShipmentStatuses.ShipmentStateDelivered,
      AllShipmentStatuses.ShipmentStateDeliveryException,
      AllShipmentStatuses.ShipmentStateReturnedToSender,
    ],
  ],
  [
    AllShipmentStatuses.ShipmentStateDeliveryException,
    [
      AllShipmentStatuses.ShipmentStateDelivered,
      AllShipmentStatuses.ShipmentStateLost,
      AllShipmentStatuses.ShipmentStateReturnedToSender,
      AllShipmentStatuses.ShipmentStateShipped,
    ],
  ],
  [
    AllShipmentStatuses.ShipmentStateReturnedToSender,
    [
      AllShipmentStatuses.ShipmentStateDelivered,
      AllShipmentStatuses.ShipmentStateDeliveryException,
      AllShipmentStatuses.ShipmentStateLost,
    ],
  ],
  [AllShipmentStatuses.ShipmentStateProcessingFulfillment, [AllShipmentStatuses.ShipmentStateCancelled]],
  [AllShipmentStatuses.ShipmentStateCancelled, []],
]);

export const getTransitions = (shipmentType: string) =>
  shipmentType === SHIPPING_TYPE.MANUAL ? ManualShipmentTransitions : ShipmentAllowedTransitions;

export const usStates: ReadonlyArray<Region> = [
  { value: 'AL', name: 'Alabama' },
  { value: 'AK', name: 'Alaska' },
  { value: 'AZ', name: 'Arizona' },
  { value: 'AR', name: 'Arkansas' },
  { value: 'CA', name: 'California' },
  { value: 'CO', name: 'Colorado' },
  { value: 'CT', name: 'Connecticut' },
  { value: 'DE', name: 'Delaware' },
  { value: 'DC', name: 'District of Columbia' },
  { value: 'FL', name: 'Florida' },
  { value: 'GA', name: 'Georgia' },
  { value: 'HI', name: 'Hawaii' },
  { value: 'ID', name: 'Idaho' },
  { value: 'IL', name: 'Illinois' },
  { value: 'IN', name: 'Indiana' },
  { value: 'IA', name: 'Iowa' },
  { value: 'KS', name: 'Kansas' },
  { value: 'KY', name: 'Kentucky' },
  { value: 'LA', name: 'Louisiana' },
  { value: 'ME', name: 'Maine' },
  { value: 'MD', name: 'Maryland' },
  { value: 'MA', name: 'Massachusetts' },
  { value: 'MI', name: 'Michigan' },
  { value: 'MN', name: 'Minnesota' },
  { value: 'MS', name: 'Mississippi' },
  { value: 'MO', name: 'Missouri' },
  { value: 'MT', name: 'Montana' },
  { value: 'NE', name: 'Nebraska' },
  { value: 'NV', name: 'Nevada' },
  { value: 'NH', name: 'New Hampshire' },
  { value: 'NJ', name: 'New Jersey' },
  { value: 'NM', name: 'New Mexico' },
  { value: 'NY', name: 'New York' },
  { value: 'NC', name: 'North Carolina' },
  { value: 'ND', name: 'North Dakota' },
  { value: 'OH', name: 'Ohio' },
  { value: 'OK', name: 'Oklahoma' },
  { value: 'OR', name: 'Oregon' },
  { value: 'PA', name: 'Pennsylvania' },
  { value: 'PR', name: 'Puerto Rico' },
  { value: 'RI', name: 'Rhode Island' },
  { value: 'SC', name: 'South Carolina' },
  { value: 'SD', name: 'South Dakota' },
  { value: 'TN', name: 'Tennessee' },
  { value: 'TX', name: 'Texas' },
  { value: 'UT', name: 'Utah' },
  { value: 'VT', name: 'Vermont' },
  { value: 'VA', name: 'Virginia' },
  { value: 'WA', name: 'Washington' },
  { value: 'WV', name: 'West Virginia' },
  { value: 'WI', name: 'Wisconsin' },
  { value: 'WY', name: 'Wyoming' },
];

export const csRegions: ReadonlyArray<Region> = [
  { name: 'Alberta', value: 'AB' },
  { name: 'British Columbia', value: 'BC' },
  { name: 'Manitoba', value: 'MB' },
  { name: 'New Brunswick', value: 'NB' },
  { name: 'Newfoundland', value: 'NF' },
  { name: 'Northwest Territories', value: 'NT' },
  { name: 'Nova Scotia', value: 'NS' },
  { name: 'Nunavut', value: 'NU' },
  { name: 'Ontario', value: 'ON' },
  { name: 'Prince Edward Island', value: 'PE' },
  { name: 'Quebec', value: 'QC' },
  { name: 'Saskatchewan', value: 'SK' },
  { name: 'Yukon Territory', value: 'YT' },
];

function getNormalizedRegionName(name: string) {
  return name?.toLowerCase()?.replace(/\s/g, '');
}

function getNormalizedRegionValue(value: string) {
  return value?.toUpperCase();
}

function getNormalizedCountryCode2(countryCode2: string) {
  return countryCode2?.toUpperCase();
}

export function getRegionBy(prop: 'name' | 'value', countryCode2: string, value: string) {
  const normalizedCountryCode2 = getNormalizedCountryCode2(countryCode2);

  if (normalizedCountryCode2 !== 'US' && normalizedCountryCode2 !== 'CA') {
    return undefined;
  }

  const regions = normalizedCountryCode2 === 'US' ? usStates : csRegions;
  const normalizedValue = prop === 'name' ? getNormalizedRegionName(value) : getNormalizedRegionValue(value);
  const findRegion =
    prop === 'name'
      ? (region: Region) => getNormalizedRegionName(region.name) === normalizedValue
      : (region: Region) => region.value === normalizedValue;
  return regions.find(findRegion);
}

export const reasonToTranslationKey: Map<RefundReasonSelection | `${RefundReasonResponse}`, string> = new Map(
  [
    ['Empty', 'empty'],
    ['LostInTransit', 'lost_in_transit'],
    ['UndeliverableRefusedReturned', 'undeliverable_refused_returned'],
    ['ShippingHandlingIssues', 'shipping_handling_issues'],
    ['WarrantyReplacement', 'warranty_replacement'],
    ['ExceptionRefundedProduct', 'exception_refunded_product'],
    ['ExceptionRefundedProductShipping', 'exception_refunded_product_and_shipping'],
    ['', 'empty'],
    ['lost-in-transit', 'lost_in_transit'],
    ['undeliverable-refused-returned', 'undeliverable_refused_returned'],
    ['shipping-handling-issues', 'shipping_handling_issues'],
    ['warranty-replacement', 'warranty_replacement'],
    ['exception-refunded-product', 'exception_refunded_product'],
    ['exception-refunded-product-shipping', 'exception_refunded_product_and_shipping'],
    ['Cancelled', 'cancelled'],
    ['cancelled', 'cancelled'],
  ]
);

export const getRegionsByCountryCode2 = (countyCode2: string) => {
  const normalizedCountryCode2 = getNormalizedCountryCode2(countyCode2);

  switch (normalizedCountryCode2) {
    case 'US':
      return usStates;
    case 'CA':
      return csRegions;
    default:
      return undefined;
  }
};

export const getRegionInfo = (value: string, countryCode: string) => {
  const stateData = getRegionsByCountryCode2(countryCode);
  const lVal = (value || '').toLowerCase().trim();
  return stateData.find((st: Region) => st.name.toLowerCase() === lVal || st.value.toLowerCase() === lVal);
};

export const countriesRequiredRegion = ['US', 'CA'];

export const isRegionRequired = (code: string) => countriesRequiredRegion.includes(code);

export const getRegionName = (region: string, country_code_2: string) =>
  (isRegionRequired(country_code_2) && getRegionInfo(region, country_code_2)?.name) || region;

export const getAddressString = ({
  recipient_firstname,
  recipient_lastname,
  recipient,
  street_line1,
  street_line2,
  street_line3,
  city,
  region,
  postal_code,
  country_code_2,
}: {
  [key: string]: string;
}) =>
  `${
    !recipient_firstname && !recipient_lastname
      ? ''
      : `${recipient_firstname || ''} ${recipient_lastname || ''}, `
  }` +
  `${!recipient ? '' : `${recipient}, `}` +
  `${!street_line1 ? '' : `${street_line1}, `}` +
  `${!street_line2 ? '' : `${street_line2}, `}` +
  `${!street_line3 ? '' : `${street_line3}, `}` +
  `${!city ? '' : `${city}, `}` +
  `${!region ? '' : `${getRegionName(region, country_code_2)}, `}` +
  `${!postal_code ? '' : `${postal_code}, `}` +
  `${!country_code_2 ? '' : country_code_2}`;

export const SERVICE_ORG_ID = window.fromServer?.env?.SERVICE_ORG_ID;

export const processOrgs = (organizations: AdminOrganizationMap): Organization[] =>
  Object.values(organizations)
    .filter((org: Organization) => org.id !== SERVICE_ORG_ID)
    .sort((org1, org2) => org1.name.toLowerCase().localeCompare(org2.name.toLowerCase()));

export const bulkSearchStr = [
  ShipmentStatus.ShipmentStateIncomplete,
  ShipmentStatus.ShipmentStateDraft,
  ShipmentStatus.ShipmentStateAwaitingValidation,
  ShipmentStatus.ShipmentStateProcessingAddress,
  ShipmentStatus.ShipmentStateAddressValid,
  ShipmentStatus.ShipmentStateAddressInvalid,
  ShipmentStatus.ShipmentStateAddressFail,
  ShipmentStatus.ShipmentStateError,
  ShipmentStatus.ShipmentStateAwaitingEstimate,
  ShipmentStatus.ShipmentStateAwaitingEstimateApproval,
].map((n) => `shipment_state_id::=::${n}`);

const getLineError = (errorText: string) => {
  if (errorText.split('line ')[1] !== undefined) {
    return parseInt(errorText.split('line ')[1].slice(0, 3).match(/\d/g).join(''), 10);
  }

  if (errorText.split('Line Number ')[1] !== undefined) {
    return parseInt(errorText.split('Line Number ')[1].slice(0, 3).match(/\d/g).join(''), 10);
  }

  if (errorText.split('Radnummer ')[1] !== undefined) {
    return parseInt(errorText.split('Radnummer ')[1].slice(0, 3).match(/\d/g).join(''), 10);
  }

  return 10000;
};

/**
 *
 * @param {string} htmlString
 * @returns {ParserResult}
 */
export const validateHtml = (htmlString: string): ParserResult => {
  if (htmlString == null || htmlString === '') {
    return {
      htmlIsValid: false,
      errorText: 'The email template should not be empty',
      errorLineStart: 0,
      errorLineEnd: 0,
      errorColumnStart: 0,
      errorColumnEnd: 0,
    };
  }

  const htmlForParser = htmlString
    .replace(
      /<(br|hr|img|meta|area|base|col|command|embed|input|keygen|link|meta|param|source|track|wbr).*?>/g,
      ''
    )
    .replace(/&nbsp;/g, '&#160;')
    .replace(/&copy;/g, '&#169;');

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlForParser, 'text/xml');
  const errorElement = doc.querySelector('parsererror');

  if (errorElement == null) {
    return {
      htmlIsValid: true,
      errorText: 'Your HTML code has no errors!',
      errorLineStart: 0,
      errorLineEnd: 0,
      errorColumnStart: 0,
      errorColumnEnd: 0,
    } as ParserResult;
  }

  const errorText = errorElement.textContent;
  const errorLineStart = 0;
  const errorLineEnd = getLineError(errorText);
  const errorColumnStart = 0;
  const errorColumnEnd = 500;

  return {
    htmlIsValid: false,
    errorText,
    errorLineStart,
    errorLineEnd,
    errorColumnStart,
    errorColumnEnd,
  };
};

export const getPurchaseOrderLabel = (purchaseOrderId: string, purchaseOrderNumber?: string) =>
  `${purchaseOrderNumber ? `${purchaseOrderNumber} - ` : ''}${purchaseOrderId?.substring(purchaseOrderId.length - 4)}`;

export const getSelectionLabel = (productname: string, customizationId: string) =>
  customizationId === null ? productname : `${productname} - ${customizationId}`;
export enum CarriersEnum {
  'usps' = 'USPS',
  'fedex' = 'FedEx',
  'fedex same day' = 'FedEx Same Day',
  'tecex' = 'TecEx',
  'others' = 'Others',
  'ups' = 'UPS',
  'bluedart' = 'Blue Dart',
  'india-post' = 'India Post',
  'dpd' = 'DPD',
  'royal mail' = 'Royal Mail',
  'pitney bowes' = 'Pitney Bowes',
  'transnet' = 'Transnet',
}

export type CarrierType = keyof typeof CarriersEnum;

export const getShipmentCarrier = (carrier: CarrierType) => {
  if (carrier in CarriersEnum) {
    return CarriersEnum[carrier];
  }
  if (carrier.toLowerCase() in CarriersEnum) {
    return CarriersEnum[carrier.toLowerCase() as CarrierType];
  }
  return carrier;
};

export enum SHIPPING_TYPE_SELECT {
  'Any' = 'Any',
  'automated' = 'Automated',
  'manual' = 'Manual',
  'fido2preregistered' = 'Fido2PreRegistered',
}

export type ShippingEnumType = keyof typeof SHIPPING_TYPE_SELECT;

export enum supportedPartners {
  sthlm = 'FP8nYJNSBcv7dLnQvx7LZn',
  pa = 'Sy3GokXw2ugcgXLm5ABRuC',
  notAllowed = 'LtcFW1AncxyNQkQkfxm8j',
}
export const FPR_SUPPORTED_PARTNERS: string[] = Object.values(supportedPartners);

export const findCountry = (countries: Country[], code: string) =>
  countries?.find((item) => item.country_code_2.toLowerCase() === code.toLowerCase());

export const emptyOrAlpha = /^$|[A-Za-z]+/;

export const containsAlpha = /[A-Za-z]+/;

export const positiveIntegers = /^\d*$/;

export function isRequestBlocking(apiState: RequestState) {
  return apiState === 'idle' || apiState === 'pending';
}

export function isEditorEvent(data: any): data is EditorEvents {
  return data && (data as EditorEvents)?.action !== undefined;
}

// Used by the email component to communicate with the email editor
// that is isolated in an iframe
export function postEditorMessage(targetWindow: Window, event: EditorEvents, targetOrigin: string) {
  targetWindow.postMessage(event, targetOrigin);
}

export function getFormattedDateString(date: string | Date) {
  if (date === undefined || date === null) {
    return undefined;
  }

  // TODO - Once we have the ability to import dayjs locales dynamically, switch this to navigator.language from 'en-US'
  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: isServiceConsole ? 'UTC' : undefined,
  }).format(new Date(date));
}

export const formatDataGridDateString: GridValueFormatter = (value: string) =>
  value ? getFormattedDateString(value) : '--';

export const getPurchaseOrderNumber: GridValueFormatter = (value: string) =>
  (JSON.parse(value) as { purchaseorder_number: string }).purchaseorder_number;

export const getShipmentStateMessage: GridValueFormatter = (value: string) =>
  (JSON.parse(value) as { shipment_state_message: string }).shipment_state_message;

export const getTrackingLink: GridValueFormatter = (value: string) =>
  (JSON.parse(value) as { tracking_link: string }).tracking_link;

export const getChannelPartnerIdByMapping = (inventoryMapping: AllInventoryMap, pIds: number[]): string =>
  Object.entries(inventoryMapping)?.find(([, val]) => pIds.some((p) => val.includes(p)))?.[0] ||
  YUBICO_CHANNEL_PARTNER.toString();

export const rolePriority = [
  'service-owner',
  'service-admin',
  'service-auditor',
  'org-owner',
  'org-shipping-admin',
  'org-auditor',
  'org-reseller',
  'org-distributor',
];

export const sortRoles = (roles: string[]) => {
  const orderedRoles = [...roles];
  orderedRoles.sort((a: string, b: string) => rolePriority.indexOf(b) - rolePriority.indexOf(a));
  return orderedRoles;
};

export const getRoleChipVariant = (orgRole: AllRoles) => {
  switch (orgRole) {
    case 'org-reseller':
      return 'reseller';
    case 'org-distributor':
      return 'distributor';
    case 'org-owner':
      return 'owner';
    case 'service-owner':
      return 'serviceowner';
    case 'org-shipping-admin':
    case 'service-admin':
      return 'admin';
    case 'org-auditor':
    case 'service-auditor':
      return 'auditor';
    default:
      throw new Error('Invalid chip variant role');
  }
};

export const DEFAULT_PAGE = 0;
export const DEFAULT_PAGE_SIZE = 15;

export const getDefaultSearchParams = (): SearchParams => ({
  sortDirection: 'DESC',
  page: DEFAULT_PAGE,
  pageSize: DEFAULT_PAGE_SIZE,
});

export const isAdvancedSearch = (query: string | Array<string>): query is Array<string> =>
  query && Array.isArray(query) && query.length > 0;

export const getCountryHandlingType = (isDefault: boolean, isManual: boolean): string => {
  if (isManual) {
    return 'country_type.special';
  }
  if (isDefault) {
    return 'country_type.default_on';
  }
  return 'country_type.default_off';
};

export const getCountryName = (countries: CountryResponseServiceAdmin[], code: string) => {
  return countries?.find((c: CountryResponseServiceAdmin) => c.country_code_2 === code)?.country_name || code;
};
export const getCountry = (countries: CountryResponseServiceAdmin[], code: string) => {
  return countries?.find((c: CountryResponseServiceAdmin) => c.country_code_2 === code);
};

export const disabledUserStates = ['INACTIVE', 'SUSPENDED'];

export const emptyPo = (): PurchaseOrderCreateRequest => ({
  organization_id: '',
  order_date: new Date().toISOString(),
  purchaseorder_number: '',
  yubicoorder_number: '',
  yubico_subsidiary: 0,
  is_post_pay: true,
  is_tax_exempt: false,
  is_override_shipping: false,
  override_shipping_charge: 0,
  override_shipping_key_limit: 0,
  key_contact: '',
  key_contact_email: '',
  closing_street_line1: '',
  closing_street_line2: '',
  closing_city: '',
  closing_region: '',
  closing_country_code_2: 'US',
  closing_postal_code: '',
  key_contact_phone_number: '',
  shipping_credit_purchased: 0,
  tax_credit_purchased: 0,
  vat_credit_purchased: 0,
  order_items: [],
  reseller_organization_id: null,
  distributor_organization_id: null,
  allow_automated_shipments: false,
  purchaseorder_id: undefined,
});

export const wholeNumberRegex = () => /^(0|[1-9][0-9]*)$/g;
export const latinCharOnlyRegex = /^[\u0020-\u007E\u00A0-\u00FF\s.,-]*$/;
export const postalCodeRegex = /^\d{5}(-\d{4})?$/; // Match either 5 digits or zip+4 format
export const nonNumericPattern = /[^0-9]/g;
export const wholeNumberPattern = '0|[1-9][0-9]*';
export const getPercentage = (value: number, total: number): number => {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total === 0) {
    return 0; // Return 0 instead of NaN
  }
  return Math.floor((value / total) * 100);
};

export const getLatinOnlyValidator = (t: TFunction<'translation', undefined>) => ({
  validate: {
    latinOnly: (value: string) => latinCharOnlyRegex.test(value) || t('validation_errors.latin_only'),
  },
});

export const getPostalCodeValidator = (t: TFunction<'translation', undefined>) => ({
  validate: {
    postalCode: (value: string) => postalCodeRegex.test(value) || t('validation_errors.postal_code'),
  },
  handleFormatting: (value: string) => {
    const strippedValue = value.replace(nonNumericPattern, '');
    if (strippedValue.length === 9) {
      const formattedPostalCode = `${strippedValue.slice(0, 5)}-${strippedValue.slice(5)}`;
      return formattedPostalCode;
    }
    return value;
  },
});

export const getNetSuiteOrderType = (subscriptionType: number): NetsuiteOrderType => {
  if (subscriptionType === 0) {
    return NetsuiteOrderType.PhysicalOrPerpetual;
  }

  return NetsuiteOrderType.Subscription15Or20;
};

// From Zod - https://github.com/colinhacks/zod/blob/main/src/types.ts#L660C1-L661C88
export const emailRegex =
  /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-\\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\\-]*\.)+[A-Z]{2,}$/i;

/**
 * Converts bytes to the largest possible unit with a precision of 2
 */
export function bytesToLargestUnit(bytes: number): ReadableFileSize {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '0.00 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'] as const;

  let index = 0;
  let remaining = bytes;
  while (remaining >= 1024 && index <= units.length - 1) {
    remaining /= 1024;
    index += 1;
  }

  return `${remaining.toFixed(2) as any} ${units[index]}`;
}

/**
 * @param status the status of the grant
 * @returns true if the grant can be deleted, false otherwise
 */
export function canDeleteGrant(status: GrantStatusEnum) {
  return (
    status === GrantStatusEnum.Invited ||
    status === GrantStatusEnum.Expired ||
    status === GrantStatusEnum.Failed
  );
}
